
const KNOWN_DEV_SECRETS = new Set([
  'riz-local-development',
  'development',
  'changeme',
  'change-me',
  'secret',
  'test',
]);

const MIN_SECRET_LENGTH = 32;

export function secretProblem(value: string | undefined): string | null {
  if (!value) return 'is not set';
  // Ahead of the length check: every known value is short, and "too short" is a
  // misleading thing to tell someone who pasted a placeholder.
  if (KNOWN_DEV_SECRETS.has(value)) return 'is a known development value';
  if (value.length < MIN_SECRET_LENGTH) return `is shorter than ${MIN_SECRET_LENGTH} characters`;
  return null;
}

type Requirement =
  | 'always'
  | 'production'
  | 'optional';

interface Rule {
  name: string;
  requirement: Requirement;
  purpose: string;
  check: (value: string) => string | null;
}

const isUrl = (value: string) => {
  try {
    new URL(value);
    return null;
  } catch {
    return 'is not a valid URL';
  }
};

const isHttpsUrl = (value: string) => {
  const problem = isUrl(value);
  if (problem) return problem;
  const url = new URL(value);
  if (url.protocol !== 'https:') return 'must use https';
  if (/^(localhost|127\.|0\.0\.0\.0|\[::1\])/.test(url.hostname)) return 'must not point at localhost';
  return null;
};

const isPositiveInt = (max: number) => (value: string) => {
  const parsed = Number(value);
  if (!Number.isInteger(parsed)) return 'is not an integer';
  if (parsed < 1) return 'must be at least 1';
  if (parsed > max) return `must be at most ${max}`;
  return null;
};

const isBooleanWord = (value: string) =>
  value === 'true' || value === 'false' ? null : "must be exactly 'true' or 'false'";

const isOriginList = (value: string) => {
  const entries = value.split(',').map((entry) => entry.trim()).filter(Boolean);
  if (entries.length === 0) return 'is empty';
  for (const entry of entries) {
    try {
      const url = new URL(entry);
      if (url.protocol !== 'http:' && url.protocol !== 'https:') return `contains a non-http(s) entry: ${entry}`;
      if (url.pathname !== '/' && url.pathname !== '') return `contains a path, not a bare origin: ${entry}`;
    } catch {
      return `contains an entry that is not a URL: ${entry}`;
    }
  }
  return null;
};

const isStrongSecret = (value: string) => secretProblem(value);

/** Accepts either `user@domain` or `Display Name <user@domain>`. */
const isMailAddress = (value: string) => {
  const address = /^[^<>]*<([^<>\s]+)>$/.exec(value)?.[1] ?? value;
  return /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/.test(address) ? null : 'is not an email address';
};

/** A Sentry DSN is `https://{public_key}@{host}/{numeric_project_id}`. */
const isSentryDsn = (value: string) => {
  const problem = isUrl(value);
  if (problem) return problem;
  const url = new URL(value);
  if (url.protocol !== 'https:') return 'must use https';
  if (!url.username) return 'is missing the public key before the @';
  if (!/^\/\d+$/.test(url.pathname)) return 'must end in a numeric project id';
  return null;
};

export const ENV_RULES: Rule[] = [
  {
    name: 'DATABASE_URL',
    requirement: 'always',
    purpose: 'Prisma opens the PostgreSQL connection from it.',
    check: isUrl,
  },
  {
    name: 'IDENTITY_HASH_SECRET',
    requirement: 'production',
    purpose: 'It keys visitor identity hashes, SDK run tokens and OTP code hashes.',
    check: isStrongSecret,
  },
  {
    name: 'IDENTITY_HASH_SECRET_PREVIOUS',
    requirement: 'optional',
    purpose:
      'During a rotation, it lets a stored browserIdHash keyed under the outgoing secret still resolve — set it to the value IDENTITY_HASH_SECRET is being rotated away from, and remove it once the rotation window closes.',
    check: isStrongSecret,
  },
  {
    name: 'APP_ORIGINS',
    requirement: 'production',
    purpose:
      'It lists the origins this application is served from, and the SDK routes reject every request whose Origin it does not name.',
    check: isOriginList,
  },
  {
    name: 'CRON_SECRET',
    requirement: 'production',
    purpose: 'It is the bearer key the scheduled maintenance routes under /api/internal/cron require, and the one apps/worker presents on every call.',
    check: isStrongSecret,
  },
  {
    name: 'NEXT_PUBLIC_APP_URL',
    requirement: 'production',
    purpose: 'It is the link target in outbound email.',
    check: isHttpsUrl,
  },
  {
    name: 'REDIS_URL',
    requirement: 'optional',
    purpose:
      'It backs the distributed rate limiter. Unset, each affected route falls back to its single-instance limiter rather than refusing to boot.',
    check: isUrl,
  },
  {
    name: 'RIZ_API_INTERNAL_URL',
    requirement: 'optional',
    purpose: 'It is the private origin used by the web application to proxy API requests.',
    check: isUrl,
  },
  {
    name: 'REWARDS_ISSUANCE_ENABLED',
    requirement: 'optional',
    purpose: 'It is the boot-time default for the 0-01 reward kill switch, and a RuntimeFlag row overrides it.',
    check: isBooleanWord,
  },
  {
    name: 'LEADERBOARD_TRUSTED_ONLY',
    requirement: 'optional',
    purpose: 'It is the boot-time default for the 0-02 competitive trust filter.',
    check: isBooleanWord,
  },
  {
    name: 'RIZ_ANALYTICS_RETENTION_DAYS',
    requirement: 'optional',
    purpose: 'It sets how long raw analytics events are kept before the purge deletes them.',
    check: isPositiveInt(3650),
  },
  {
    name: 'RIZ_OUTBOX_RETENTION_DAYS',
    requirement: 'optional',
    purpose: 'It sets how long processed outbox rows are kept.',
    check: isPositiveInt(3650),
  },
  {
    name: 'RIZ_ENGAGEMENT_RETENTION_DAYS',
    requirement: 'optional',
    purpose: 'It sets how long engagement events are kept.',
    check: isPositiveInt(3650),
  },
  {
    name: 'RESEND_API_KEY',
    requirement: 'optional',
    purpose:
      'It authenticates the Resend API, the primary transport for OTP and welcome email, and SMTP is used when it is unset.',
    check: (value) =>
      /^re_[A-Za-z0-9_-]+$/.test(value) ? null : "must be a Resend API key, which begins with 're_'",
  },
  {
    name: 'MAIL_FROM',
    requirement: 'optional',
    purpose:
      'It is the sender address on outbound mail, and Resend refuses any address outside a domain verified in the account.',
    check: isMailAddress,
  },
  {
    name: 'SMTP_HOST',
    requirement: 'optional',
    purpose: 'It configures the SMTP fallback, used when Resend is unconfigured or the API call fails.',
    check: (value) => (value.includes(' ') ? 'must not contain whitespace' : null),
  },
  {
    name: 'SMTP_PORT',
    requirement: 'optional',
    purpose: 'It is the fallback SMTP port.',
    check: isPositiveInt(65535),
  },
  {
    name: 'SMTP_SECURE',
    requirement: 'optional',
    purpose: 'It selects whether the fallback SMTP connection uses implicit TLS.',
    check: isBooleanWord,
  },
  {
    name: 'SMTP_FROM',
    requirement: 'optional',
    purpose: 'It is the sender address on fallback SMTP mail, and MAIL_FROM is used when it is unset.',
    check: isMailAddress,
  },
  {
    name: 'SENTRY_DSN',
    requirement: 'optional',
    purpose:
      'It is where sendAlert forwards fraud, ledger, and analytics alerts; unset means alerts still log locally but never reach Sentry.',
    check: isSentryDsn,
  },
];

export interface EnvProblem {
  name: string;
  message: string;
  fatal: boolean;
}

function isBuildPhase() {
  return process.env.NEXT_PHASE === 'phase-production-build';
}

export function validateEnv(
  source: NodeJS.ProcessEnv = process.env,
  { production = source.NODE_ENV === 'production' }: { production?: boolean } = {},
): EnvProblem[] {
  const problems: EnvProblem[] = [];

  for (const rule of ENV_RULES) {
    const raw = source[rule.name];
    const required = rule.requirement === 'always' || (rule.requirement === 'production' && production);

    if (raw === undefined || raw === '') {
      if (required) {
        problems.push({
          name: rule.name,
          message: `${rule.name} is not set. ${rule.purpose}`,
          fatal: true,
        });
      }
      continue;
    }

    const problem = rule.check(raw);
    if (problem) {
      problems.push({
        name: rule.name,
        message: `${rule.name} ${problem}. ${rule.purpose}`,
        fatal: true,
      });
    }
  }

  return problems;
}

export function formatEnvProblems(problems: EnvProblem[]): string {
  return problems.map((problem) => `  - ${problem.message}`).join('\n');
}

export function assertEnv() {
  if (isBuildPhase()) return;

  const production = process.env.NODE_ENV === 'production';
  const problems = validateEnv(process.env, { production });
  if (problems.length === 0) return;

  const report = formatEnvProblems(problems);
  if (production) {
    throw new Error(`Environment validation failed:\n${report}`);
  }
  console.warn(`[env] configuration problems (fatal in production):\n${report}`);
}
